/**
 * Squares for a step of `dx` across and `dy` down, by the diagonal rule: `alternate` counts every second diagonal two
 * (the first one, the second two), `one` every diagonal one, `two` every diagonal two, and `straight` is the straight
 * line's length in squares (not rounded here: an area compares it with its size; a label shows it to a tenth).
 */
export function squaresFor(dx, dy, diagonal) {
    const ax = Math.abs(dx);
    const ay = Math.abs(dy);
    const d = Math.min(ax, ay);
    const s = Math.max(ax, ay) - d;
    switch (diagonal) {
        case 'alternate': return s + d + Math.floor(d / 2);
        case 'one': return s + d;
        case 'two': return s + 2 * d;
        case 'straight': return Math.hypot(ax, ay);
    }
}
/**
 * Squares with height (qa-runs#1160; design/32 §3): height is one more direction on the grid, counted by the same rule —
 * the squares across the map first, then that count and the squares of height `dz` as a second diagonal. Under
 * `alternate`, 6 across and 4 up is 8 (30 ft and 20 ft: 40 ft); 3 across and 8 up is 9 (45 ft). With no height it is
 * exactly `squaresFor`, so a scene with no height measures as it always did.
 */
export function squares3D(dx, dy, dz, diagonal) {
    const flat = squaresFor(dx, dy, diagonal);
    return dz ? squaresFor(flat, dz, diagonal) : flat;
}
/** A height difference in board squares rounded to whole squares, for the grid rules that count whole steps (`straight` keeps it exact). */
export const heightSquares = (dz, diagonal) => (diagonal === 'straight' ? Math.abs(dz) : Math.round(Math.abs(dz)));
/** Squares from one square to another by the diagonal rule, counting the height between them (squares; 0 when absent). */
export function squaresBetween3D(a, az, b, bz, diagonal) {
    return squares3D(a.i - b.i, a.j - b.j, heightSquares(az - bz, diagonal), diagonal);
}
/** A move's cost with the height it climbed or dropped (design/32 §3): the route's squares and the height, by the rule. */
export function withClimb(cost, dz, diagonal) {
    const h = heightSquares(dz, diagonal);
    if (!h)
        return cost;
    return { ...cost, squares: Math.round(squaresFor(cost.squares, h, diagonal) * 1000) / 1000 };
}
/** Squares from one square to another by the diagonal rule. */
export function squaresBetween(a, b, diagonal) {
    return squaresFor(a.i - b.i, a.j - b.j, diagonal);
}
const tenth = (n) => Math.round(n * 10) / 10;
const squaresWord = (n) => `${tenth(n)} ${n === 1 ? 'square' : 'squares'}`;
/** An amount in the system's unit, to a hundredth ("7.5 m"). */
const inUnit = (squares, d) => `${Math.round(squares * d.perSquare * 100) / 100} ${d.unit}`;
/** "30 ft (6 squares)"; "6 squares" with no unit. */
export const distanceLabel = (squares, distance) => (distance ? `${inUnit(squares, distance)} (${squaresWord(squares)})` : squaresWord(squares));
/** What one square is in the system's unit ("5 ft", "1.5 m"), or "one square" with no unit. */
export const squareLabel = (distance) => (distance ? inUnit(1, distance) : 'one square');
/**
 * An area's size as the table says it (sizes are squares since issue 1303): "20-ft burst", "20 × 15 ft rectangle" in
 * the system's unit; "4-square burst", "4 × 3-square rectangle" with none.
 */
export function areaSizeLabel(a, distance) {
    const n = (squares) => (distance ? Math.round(squares * distance.perSquare * 100) / 100 : squares);
    const unit = distance ? distance.unit : 'square';
    if (a.shape === 'rect')
        return distance ? `${n(a.size)} × ${n(a.size2)} ${unit} rectangle` : `${n(a.size)} × ${n(a.size2)}-${unit} rectangle`;
    return `${n(a.size)}-${unit} ${a.shape}`;
}
/** A stored cost, strict: non-negative finite squares (difficult no more than spent), the multiplier in the format's bounds or null. */
export function parseMoveCost(v) {
    if (typeof v !== 'object' || v === null)
        return null;
    const { squares, difficult, multiplier } = v;
    const n = (x) => typeof x === 'number' && Number.isFinite(x) && x >= 0 && x <= 1_000_000;
    if (!n(squares) || !n(difficult) || difficult > squares)
        return null;
    if (multiplier !== null && !(typeof multiplier === 'number' && Number.isFinite(multiplier) && multiplier > 1 && multiplier <= 10))
        return null;
    return { squares, difficult, multiplier: multiplier };
}
/** The squares entered going from `a` to `b`, one square a step along the straight line between them (diagonals where it runs so). */
export function stepsBetween(a, b) {
    const n = Math.max(Math.abs(b.i - a.i), Math.abs(b.j - a.j));
    const out = [];
    for (let k = 1; k <= n; k += 1)
        out.push({ i: a.i + Math.round(((b.i - a.i) * k) / n), j: a.j + Math.round(((b.j - a.j) * k) / n) });
    return out;
}
/**
 * The cost of a path through `cells` (the first is where it starts; two not side by side are walked by `stepsBetween`).
 * An ordinary step costs what the diagonal rule says, the alternation carried along the whole path, so a path with no
 * difficult terrain costs exactly what the ruler measures. A step INTO a square of difficult terrain costs its base ×
 * the multiplier, the base being 1 straight and, diagonally, `alternate` 1.5 (the rule's own average, so the books' "a
 * diagonal move into difficult terrain counts as 3 squares" at ×2), `one` 1, `two` 2, and `straight` its share of the
 * line's length; such a diagonal does not move the alternation. Under `straight` each leg's steps share its measured length.
 */
export function pathCost(cells, diagonal, isDifficult, multiplier) {
    let squares = 0;
    let difficult = 0;
    let diagonals = 0;
    for (let k = 1; k < cells.length; k += 1) {
        const steps = stepsBetween(cells[k - 1], cells[k]);
        const share = steps.length ? Math.hypot(cells[k].i - cells[k - 1].i, cells[k].j - cells[k - 1].j) / steps.length : 0;
        let from = cells[k - 1];
        for (const c of steps) {
            const diag = c.i !== from.i && c.j !== from.j;
            from = c;
            if (multiplier !== null && isDifficult(c)) {
                const base = diagonal === 'straight' ? share : !diag ? 1 : diagonal === 'alternate' ? 1.5 : diagonal === 'two' ? 2 : 1;
                squares += base * multiplier;
                difficult += base;
                continue;
            }
            if (diagonal === 'straight') {
                squares += share;
                continue;
            }
            if (!diag) {
                squares += 1;
                continue;
            }
            diagonals += 1;
            squares += diagonal === 'two' ? 2 : diagonal === 'one' ? 1 : diagonals % 2 === 0 ? 2 : 1;
        }
    }
    const tidy = (n) => Math.round(n * 1000) / 1000;
    return { squares: tidy(squares), difficult: tidy(difficult), multiplier };
}
/** A move's cost as the table says it: "30 ft (6 squares)", or with difficult terrain "30 ft (10 ft difficult ×2)". */
export function moveLabel(cost, distance) {
    if (!cost.difficult || cost.multiplier === null)
        return distanceLabel(cost.squares, distance);
    const say = (n) => (distance ? inUnit(n, distance) : squaresWord(n));
    return `${say(cost.squares)} (${say(cost.difficult)} difficult ×${cost.multiplier})`;
}
/**
 * The ruler's label from one square to another: by the system's rule, or — with no distance rule — the squares across
 * and down ("4 across, 3 down"; one count when it runs straight), since no diagonal rule is assumed. Given the squares of
 * difficult terrain this screen holds (issue 1151), a line through them is costed as a move along it would be.
 */
export function rulerLabel(a, b, distance, isDifficult) {
    if (distance && isDifficult && distance.difficult)
        return moveLabel(pathCost([a, b], distance.diagonal, isDifficult, distance.difficult.multiplier), distance);
    if (distance)
        return distanceLabel(squaresBetween(a, b, distance.diagonal), distance);
    const dx = Math.abs(a.i - b.i);
    const dy = Math.abs(a.j - b.j);
    return dx === 0 || dy === 0 ? squaresWord(dx + dy) : `${dx} across, ${dy} down`;
}
/**
 * A measure on a map with no grid (issue 1035, GRID-005): the straight distance in the system's unit, to a tenth. A
 * square is the grid's square size, which the DM still sets in grid setup — so the default 70 px is one square, and a
 * map drawn at 50 px to a square is set by giving its squares 50 px. With no distance rule it is in squares.
 */
export function rawDistanceLabel(a, b, grid, distance) {
    const squares = Math.hypot((b.x - a.x) / grid.x.basePitch, (b.y - a.y) / grid.y.basePitch);
    return distance ? `${tenth(squares * distance.perSquare)} ${distance.unit}` : squaresWord(squares);
}
//# sourceMappingURL=measure.js.map